import {
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, forkJoin, Subject } from "rxjs";
import { ICredentialItem } from "../../models/credentialItem";
import { IState } from "../../models/state";
import { ViewStateService } from "../../../../shared/services/view-state.service";
import { DirectoryService } from "../../../shared/services/directory.service";
import { SearchParameters } from "../../../shared/models/search-parameters";
import { GlobalValidators } from "./../../../../shared/validators/global-validators";
import { RegexPatterns } from "./../../../../shared/regex/regex-patterns";
import { AppSettingsService } from "./../../../../shared/services/app-settings.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-search-form",
  templateUrl: "./search-form.component.html",
})
export class SearchFormComponent implements OnInit, OnDestroy {
  @Input() searchParams: BehaviorSubject<SearchParameters>;

  private dateInputFormat = this.appSettings.IsCompatibilityMode
    ? "MM/DD/YYYY"
    : "YYYY-MM-DD";
  private destroy$ = new Subject<void>();

  activeSearchForm: FormGroup;
  states: IState[] = [];
  credentialItems: ICredentialItem[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private viewState: ViewStateService,
    private directoryService: DirectoryService,
    public appSettings: AppSettingsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();

    this.getSearchParamOptions();

    this.buildForm();
  }

  private getSearchParamOptions() {
    forkJoin([
      this.directoryService.getStates(),
      this.directoryService.getCredentials(),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res[0]) this.states = res[0];
        if (res[1]) this.credentialItems = res[1];
        this.viewState.stopLoading();
      });
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();    
    this.formValues = updatedFormValues;
    this.validateForm();
    if (this.activeSearchForm.valid) this.searchParams.next(updatedFormValues);
  }

  clearForm() {
    this.activeSearchForm.reset();
    this.activeSearchForm.patchValue({
      state: "",
      credential: "ALL",
      birthdate: "",
      city: "",
      lastName: "",
      firstName: "",
    });
    this.formValues.clear = "true";
    this.searchParams.next(this.formValues);
  }

  private buildForm(): void {
    this.activeSearchForm = this.formBuilder.group({
      firstName: [
        this.formValues.firstName,
        [
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.alphaNumericSpecial),
        ],
      ],
      lastName: [
        this.formValues.lastName,
        [
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.alphaNumericSpecial),
        ],
      ],
      credential: [this.formValues.credential],
      city: [
        this.formValues.city,
        [
          Validators.maxLength(35),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      state: [
        this.formValues.state,
        [
          Validators.maxLength(50),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      birthdate: [
        this.formValues.birthdate,
        [GlobalValidators.validDate(this.dateInputFormat)],
      ],
    });
  }

  private validateForm() {
    if (!this.activeSearchForm) {
      return;
    }
    var hasErrors = false;

    for (const field in this.formErrors) {
      // clear previous error message (if any)
      this.formErrors[field] = "";
      const control = this.activeSearchForm.get(field);
      if (control && !control.valid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }

    if (hasErrors) this.viewState.stopLoading();
  }

  private formValues = {
    firstName: "",
    lastName: "",
    credential: "ALL",
    city: "",
    state: "",
    birthdate: "",
    clear: "",
  };

  formErrors = {
    firstName: "",
    lastName: "",
    credential: "",
    city: "",
    state: "",
    birthdate: "",
  };

  validationMessages = {
    firstName: {
      maxlength: "First Name must be max 30 characters.",
      pattern: "First Name must be letters only.",
    },
    lastName: {
      maxlength: "Last Name must be max 30 characters.",
      pattern: "Last Name must be letters only.",
    },
    credential: "",
    city: {
      maxlength: "City must be max 35 characters.",
      pattern: "City must be letters only.",
    },
    state: {
      maxlength: "State must be max 50 characters.",
      pattern: "State must be letters only.",
    },
    birthdate: {
      validDate: "Birth Date must be a valid date.",
    },
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
